/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 * Product Content Cards component
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.11.0
 */

import { ReactElement, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { makeSelectTranslationPageProducts } from '@/main/modules/internationalization/intSelectors';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import { makeSelectFeatureEnabled } from '../../PageAuth/selectors/authSelectors';
import { EProductsCategory } from '../tariffsAndServicesConstants';
import { TProductItem } from '../tariffsAndServicesProductTypes';
import { makeSelectRoamingOptions, makeSelectRoamingOptionsIsLoading, makeSelectSelectedCountryCode } from '../tariffsAndServicesSelectors';
import { IProductsMeta } from '../tariffsAndServicesTypes';
import { addRowParamsToProductList } from '../utils/addRowParamsToProductList';
import { RoamingOptionCard } from './RoamingOptions/RoamingOptionCard';
import { RoamingOptionsControl } from './RoamingOptions/RoamingOptionsControl';
import {
    ProductsCardsNoSearchMatterStyled,
    ProductsCardsStyled,
    ProductsCategoryStyled,
} from './styled/ProductsContentStyled';

export interface IProductContentServices {
    categorySelected: number;
    noResultsFilter?: string;
    productList: TProductItem[];
    productMeta: IProductsMeta;
    renderCard: (item: TProductItem, index: number) => ReactElement;
    title: string;
    isConnected?: boolean;
}

const ProductContentCards = (props: IProductContentServices) => {
    const { categorySelected, noResultsFilter, productList, productMeta, renderCard, title, isConnected } = props;
    const { roamingCategoryId } = productMeta;
    const roamingOptions = useSelector(makeSelectRoamingOptions());
    const roamingCountryCode = useSelector(makeSelectSelectedCountryCode());
    const { recommendedServicesTitle, noRoamingOptionsForThisCountry, loading } =
        useSelector(makeSelectTranslationPageProducts()).roamingOptions;
    const roamingOptionsIsLoading = useSelector(makeSelectRoamingOptionsIsLoading());
    const isRoamingProductsFeatureEnabled =
            useSelector(makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS));
    const isRoamingOptionsLoaded = roamingCountryCode && !roamingOptionsIsLoading;
    const isRoamingOptionsShown =
        !!categorySelected &&
        categorySelected === roamingCategoryId &&
        isRoamingProductsFeatureEnabled &&
        !isConnected;

    const isNoResultTextShown = (noResultsFilter && !productList.length ||
        isRoamingOptionsLoaded && !roamingOptions.length) && categorySelected !== EProductsCategory.SELECTED;

    const noResultsText = useMemo(() => {
        if (!isRoamingProductsFeatureEnabled) {
            return noResultsFilter;
        }
        if (!roamingOptionsIsLoading) {
            return isNoResultTextShown && !!roamingCountryCode && roamingOptions.length === 0
                ? noRoamingOptionsForThisCountry
                : noResultsFilter;
        } else {
            return '';
        }
    },[
        isNoResultTextShown,
        roamingOptionsIsLoading,
        noResultsFilter,
        noRoamingOptionsForThisCountry,
        roamingCountryCode,
        roamingOptions.length,
        isRoamingProductsFeatureEnabled,
    ]);

    const productListWithRowParams = addRowParamsToProductList(productList);

    return (
        <ProductsCategoryStyled className={`product-category-${categorySelected}`}>
            <h3>{title}</h3>
            {isRoamingOptionsShown && (
                <>
                    <RoamingOptionsControl/>
                    {isRoamingOptionsLoaded &&
                    <ProductsCardsStyled productMeta={productMeta}>
                        {roamingOptions.map(option =>
                            <RoamingOptionCard
                                key={option.id}
                                {...option}
                            />,
                        )}
                    </ProductsCardsStyled>
                    }
                    {roamingOptionsIsLoading && (
                        <ProductsCardsNoSearchMatterStyled variant='h4'>{loading}</ProductsCardsNoSearchMatterStyled>
                    )}
                    {!roamingCountryCode &&
                        <ProductsCardsNoSearchMatterStyled variant='h4'>{recommendedServicesTitle}</ProductsCardsNoSearchMatterStyled>
                    }
                </>
            )}
            { isNoResultTextShown && (
                <ProductsCardsNoSearchMatterStyled variant='h4'>{noResultsText}</ProductsCardsNoSearchMatterStyled>
            )}
            {!roamingCountryCode &&
                <ProductsCardsStyled productMeta={productMeta}>
                    {productListWithRowParams.map(renderCard)}
                </ProductsCardsStyled>
            }
        </ProductsCategoryStyled>
    );
};

export default ProductContentCards;
